/* eslint-disable complexity */
import {
  appAuthenticator,
  AuthenticatorUser,
  PROMISE_REQUEST,
  Logger,
  ROUTES,
  CognitoUser
} from '@futrli/shared-services';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  getCurrentAuthenticatedUser,
  setCurrentAuthenticatedUserFulfilled,
  setCurrentAuthenticatedUserPending,
  setCurrentAuthenticatedUserRejected
} from '../../auth/redux-utils/actions';
import { getTracker } from '../../core/injected';
import { DispatchAny, StoreState } from '../../core/types';
import { SIGN_IN, SIGN_OUT, CONFIRM_SIGN_IN } from '../../login/constants';
import { USERNAME_STORAGE_KEY } from '../../storage/constants';
import { Storage } from '../../storage/storage';
import { isProdEnvironment } from '../../../../skeleton/src/utils/env-utils';
import { NavigateFunction } from 'react-router-dom';

export const signInStarter = async (username: string, password: string) => {
  const user: AuthenticatorUser = await appAuthenticator.signIn(username, password);
  return {
    user,
    isMfa: user.challengeName === 'SMS_MFA'
  };
};

/**
 * Sign in an user into the app.
 * @param {History} history - allows redirect to the correct path of the app
 * @param {string} username
 * @param {string} password
 * @param {() => void} onRedirect - on success redirect callback
 * @returns {ThunkDispatch<StoreState, unknown, Action> | Error } thunk dispatch function or error
 */
export const signIn = (
  navigate: NavigateFunction,
  username: string,
  password: string,
  onRedirect?: () => Promise<void>
): DispatchAny => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    let user: AuthenticatorUser = await dispatch({
      type: SIGN_IN.ACTION,
      payload: appAuthenticator.signIn(username, password),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    try {
      setCurrentAuthenticatedUserPending()(dispatch);
      user = await getCurrentAuthenticatedUser()(dispatch);

      const tracker = getTracker();
      tracker.resetIntercom();

      !onRedirect ? navigate(ROUTES.ROOT_ROUTE) : await onRedirect();

      if (isProdEnvironment()) {
        window.utag?.link({
          event: 'form',
          event_name: 'form_submit_success',
          form_name: 'login'
        });
      }
      setCurrentAuthenticatedUserFulfilled(user)(dispatch);
    } catch {
      if (isProdEnvironment()) {
        window.utag?.link({
          event: 'form',
          event_name: 'form_submit_error',
          form_name: 'login'
        });
      }
      setCurrentAuthenticatedUserRejected()(dispatch);
    }

    Storage.getInstance().remove(USERNAME_STORAGE_KEY);

    return user;
  };
};

/**
 * Sign out an user from the app.
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const signOut = (): DispatchAny => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const tracker = getTracker();

    try {
      tracker.reset();
    } catch (error) {
      Logger.error(String(error));
    }

    return dispatch({
      type: SIGN_OUT.ACTION,
      payload: appAuthenticator.signOut(),
      meta: {
        flag: PROMISE_REQUEST
      }
    });
  };
};

/**
 * Confirm MFA code and sign in user.
 * @param {CognitoUser} loggedInUser - Cognito user with all methods
 * @param {History} history - allows redirect to the correct path of the app
 * @param {() => void} onRedirect - on success redirect callback
 * @returns {ThunkDispatch<StoreState, unknown, Action> | Error } thunk dispatch function or error
 */
export const confirmSignIn = (
  loggedInUser: CognitoUser,
  navigate: NavigateFunction,
  verificationCode: string,
  onRedirect?: () => Promise<void>
): DispatchAny => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    try {
      await dispatch({
        type: CONFIRM_SIGN_IN.ACTION,
        payload: appAuthenticator.confirmSignIn(loggedInUser, verificationCode, 'SMS_MFA'),
        meta: {
          flag: PROMISE_REQUEST
        }
      });

      setCurrentAuthenticatedUserPending()(dispatch);
      const user = await getCurrentAuthenticatedUser()(dispatch);

      const tracker = getTracker();
      tracker.resetIntercom();

      !onRedirect ? navigate(ROUTES.ROOT_ROUTE) : await onRedirect();
      if (isProdEnvironment()) {
        window.utag?.link({
          event: 'form',
          event_name: 'form_submit_success',
          form_name: 'login'
        });
      }
      setCurrentAuthenticatedUserFulfilled(user)(dispatch);
    } catch {
      if (isProdEnvironment()) {
        window.utag?.link({
          event: 'form',
          event_name: 'form_submit_error',
          form_name: 'login'
        });
      }
      setCurrentAuthenticatedUserRejected()(dispatch);
    }

    Storage.getInstance().remove(USERNAME_STORAGE_KEY);
  };
};
